import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import type { DocumentTypeSchema, FieldDefinition } from "@noa/api-core";
import { BASE_FIELDS, schemaFormSchema, type SchemaForm, type SchemaTemplate } from "./schema-admin";

export const PARSER_OPTIONS = [
  { value: "", label: "Default (auto-detect)" },
  { value: "PDF", label: "PDF (Docling)" },
  { value: "PDF_FAST", label: "PDF Fast (PyMuPDF)" },
  { value: "IMAGE", label: "Image (Vision)" },
];

export const TYPE_OPTIONS = [
  { value: "", label: "string (default)" },
  { value: "string", label: "string" },
  { value: "number", label: "number" },
  { value: "date", label: "date" },
  { value: "list[string]", label: "list[string]" },
  { value: "enum", label: "enum" },
];

export const VALIDATION_PHASE_OPTIONS = [
  { value: "", label: "Default" },
  { value: "pre_parse", label: "Pre-parse" },
  { value: "post_parse", label: "Post-parse" },
];

export const CHUNKING_STRATEGY_VALUES = [
  "",
  "hierarchical",
  "all_sections",
  "all_caps_sections",
  "numbered_items",
] as const;

export const EMPTY_FIELD: FieldDefinition = {
  name: "",
  description: "",
  type: undefined,
  extraction: {},
  vectorstore: { filterable: false, searchable: false },
};

export const EMPTY_SCHEMA_FORM: SchemaForm = {
  schema_id: "",
  doc_type: "",
  description: "",
  prompt_hint: "",
  version: 1,
  parser: "",
  extract_page_images: false,
  chunking: undefined,
  fields: [],
};

// Form sends null for empty optional fields, but zod's .optional() only accepts undefined.
export const nullsToUndefined = <T>(obj: T): T => {
  if (obj === null) return undefined as T;
  if (Array.isArray(obj)) return obj.map(nullsToUndefined) as T;
  if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, nullsToUndefined(v)])) as T;
  }
  return obj;
};

// Select components send "" for "no selection", but zod enums reject empty strings.
// Convert "" to undefined for known optional enum fields.
const OPTIONAL_ENUM_KEYS = new Set(["validation_phase", "output_type"]);
export const emptyEnumsToUndefined = <T>(obj: T): T => {
  if (Array.isArray(obj)) return obj.map(emptyEnumsToUndefined) as T;
  if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        OPTIONAL_ENUM_KEYS.has(k) && v === "" ? undefined : emptyEnumsToUndefined(v),
      ])
    ) as T;
  }
  return obj;
};

export const nullSafeResolver: Resolver<SchemaForm> = async (values, context, options) => {
  const cleaned = emptyEnumsToUndefined(nullsToUndefined(values));
  return zodResolver(schemaFormSchema)(cleaned, context, options);
};

/**
 * Form values for the modal's three entry points: edit an existing schema,
 * create from a template, or create blank (pre-seeded with BASE_FIELDS).
 * Returns null when there is nothing to load (edit mode without a schema),
 * so the caller can skip the reset — same behavior as the original branches.
 */
export function initialFormValues(
  isEdit: boolean,
  schema: DocumentTypeSchema | null,
  template?: SchemaTemplate | null
): SchemaForm | null {
  if (isEdit) {
    if (!schema) return null;
    return {
      schema_id: schema.schema_id,
      doc_type: schema.doc_type,
      description: schema.description,
      prompt_hint: schema.prompt_hint ?? "",
      version: schema.version ?? 1,
      parser: schema.parser ?? "",
      extract_page_images: schema.extract_page_images ?? false,
      chunking: schema.chunking,
      fields: (schema.fields ?? []) as SchemaForm["fields"],
    };
  }
  if (template) {
    return {
      schema_id: "",
      doc_type: template.schema.doc_type,
      description: template.schema.description,
      prompt_hint: template.schema.prompt_hint ?? "",
      version: template.schema.version,
      parser: template.schema.parser ?? "",
      extract_page_images: template.schema.extract_page_images,
      chunking: template.schema.chunking,
      fields: (template.schema.fields ?? []) as SchemaForm["fields"],
    };
  }
  return { ...EMPTY_SCHEMA_FORM, fields: BASE_FIELDS as SchemaForm["fields"] };
}

/** Drop empty/blank extraction keys so the payload only carries what the user set. */
export const cleanExtraction = (extraction: FieldDefinition["extraction"]) => {
  if (!extraction) return undefined;
  const cleaned: FieldDefinition["extraction"] = {};
  if (extraction.from_metadata_key) cleaned.from_metadata_key = extraction.from_metadata_key;
  if (extraction.default_value !== undefined && extraction.default_value !== "") {
    cleaned.default_value = extraction.default_value;
  }
  if (extraction.from_path_pattern) cleaned.from_path_pattern = extraction.from_path_pattern;
  if (extraction.from_path_prefix && Object.keys(extraction.from_path_prefix).length > 0) {
    cleaned.from_path_prefix = extraction.from_path_prefix;
  }
  if (extraction.from_filename_pattern) cleaned.from_filename_pattern = extraction.from_filename_pattern;
  if (extraction.from_llm?.prompt_hint) {
    cleaned.from_llm = {
      prompt_hint: extraction.from_llm.prompt_hint,
      output_type: extraction.from_llm.output_type,
      examples: extraction.from_llm.examples,
    };
  }
  if (extraction.from_field) {
    cleaned.from_field = extraction.from_field;
    if (extraction.transform) cleaned.transform = extraction.transform;
  }
  if (extraction.value_map && Object.keys(extraction.value_map).length > 0) {
    cleaned.value_map = extraction.value_map;
  }
  if (extraction.regex_flags && extraction.regex_flags.length > 0) {
    cleaned.regex_flags = extraction.regex_flags;
  }
  // Not editable in the UI, but present on builtin schemas — must survive
  // the edit round-trip instead of being silently deleted
  if (extraction.from_content_pattern) cleaned.from_content_pattern = extraction.from_content_pattern;
  if (extraction.path_match_options) cleaned.path_match_options = extraction.path_match_options;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

/** Drop unset vectorstore flags/templates so the payload stays minimal. */
export const cleanVectorstore = (vectorstore: FieldDefinition["vectorstore"]) => {
  if (!vectorstore) return undefined;
  const cleaned: FieldDefinition["vectorstore"] = {};
  if (vectorstore.filterable) cleaned.filterable = true;
  if (vectorstore.searchable) cleaned.searchable = true;
  if (vectorstore.temporal) cleaned.temporal = true;
  if (vectorstore.prompt_template) cleaned.prompt_template = vectorstore.prompt_template;
  if (vectorstore.embedding_template) cleaned.embedding_template = vectorstore.embedding_template;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

/** Map submitted form fields to the API FieldDefinition payload shape. */
export const toFieldDefinitions = (fields: SchemaForm["fields"]): FieldDefinition[] =>
  (fields ?? []).map((f) => ({
    name: f.name,
    description: f.description || undefined,
    type: (f.type || undefined) as FieldDefinition["type"],
    required: f.required || undefined,
    if_present_require: f.if_present_require?.length ? f.if_present_require : undefined,
    allowed_values: f.type === "enum" && f.allowed_values?.length ? f.allowed_values : undefined,
    validation_phase: f.validation_phase || undefined,
    extraction: cleanExtraction(f.extraction as FieldDefinition["extraction"]),
    vectorstore: cleanVectorstore(f.vectorstore),
  }));
